import React from "react";
import Decimal from "decimal.js";
import CurrencyField from "../../fields/currency_field";
import {
  first_mortgage_payment_p_and_i,
  loan_amount_first_mortgage,
  loan_amount_second_mortgage,
  second_mortgage_payment_p_and_i,
  second_mortgage_rate,
  is_new_second_lien
} from "../../../calculations/loan";
import PercentField from "../../fields/percent_field";
import { ARM_TERMS } from "../../../constants/loan_terms";
import NumberField from "../../fields/number_field";
import { YES, YES_NO_TBD } from "../../../constants/yes_no";
import {
  arm_adjust_as_high_as_rate,
  arm_adjust_starting_in_year
} from "../../../calculations/trid";
import { useDocument } from "../loan_tool";
import { INTEREST_ONLY } from "../../../constants/loan_payment_type";
import { VARIABLE_RATE } from "../../../constants/loan_rates";
import { REFINANCE } from "../../../constants/document_types";
import { month_to_year } from "../../../calculations/utils";
import { is_buydown, buydown_full_rate_year } from "../../../calculations/buydown";
import { STEP_RATE, STEP_PAYMENT } from "../../../constants/buydown";

export default function LoanTerms({ loan }) {
  const [
    { property, property_to_be_sold, is_selling_property },
    ,
    { documentType }
  ] = useDocument();
  const isArm = ARM_TERMS.includes(loan.term);
  const isInterestOnly = loan.payment_type === INTEREST_ONLY;
  const isBuydown = is_buydown(loan);
  const isBuydownRate =
    isBuydown && loan.buydown_disclose_product === STEP_RATE;
  const isBuydownPayment =
    isBuydown && loan.buydown_disclose_product === STEP_PAYMENT;
  const buydownFullYear = isBuydown ? buydown_full_rate_year(loan) : 0;

  // A HELOC / Fixed Rate Second discloses the NEW 2nd loan (amount, rate, P&I) -
  // the existing 1st stays and is not what this Loan Estimate is for.
  const secondLien = is_new_second_lien(loan);
  // A HELOC is a VARIABLE-rate 2nd, so its rate and payment CAN increase after
  // closing. A Fixed Rate Second is fixed, so it stays "No".
  const isVariableSecond =
    secondLien && loan.second_mortgage_loan_rate_type === VARIABLE_RATE;
  const rateCanIncrease = isArm || isBuydownRate || isVariableSecond;
  const piCanIncrease =
    isArm || isInterestOnly || isBuydown || isVariableSecond;
  const disclosedLoanAmount = secondLien
    ? loan_amount_second_mortgage(
        documentType,
        loan,
        property,
        property_to_be_sold,
        is_selling_property
      )
    : loan_amount_first_mortgage(
        documentType,
        loan,
        property,
        property_to_be_sold,
        is_selling_property
      );
  const disclosedRate = secondLien
    ? second_mortgage_rate(loan).toString()
    : loan.rate;
  const disclosedPrincipalAndInterest = secondLien
    ? second_mortgage_payment_p_and_i(
        documentType,
        loan,
        property,
        property_to_be_sold,
        is_selling_property
      )
    : first_mortgage_payment_p_and_i(
        documentType,
        loan,
        property,
        property_to_be_sold,
        is_selling_property
      );

  // A 1st+2nd combo refinance ("Equity Remaining w/ 2nd Loan"): this Loan
  // Estimate discloses the new 1st (above), and we ALSO list the new 2nd's
  // amount, rate and payment so the borrower sees the full picture. A variable
  // (HELOC) 2nd answers "Yes" and shows the variable-rate verbiage; a fixed 2nd
  // answers "No".
  const carriesNewSecond =
    documentType === REFINANCE &&
    !secondLien &&
    second_mortgage_payment_p_and_i(
      documentType,
      loan,
      property,
      property_to_be_sold,
      is_selling_property
    ).gt(0);
  const isVariableSecondCombo =
    carriesNewSecond && loan.second_mortgage_loan_rate_type === VARIABLE_RATE;
  const secondLoanAmount = carriesNewSecond
    ? loan_amount_second_mortgage(
        documentType,
        loan,
        property,
        property_to_be_sold,
        is_selling_property
      )
    : new Decimal(0);
  const secondLoanRate = carriesNewSecond
    ? second_mortgage_rate(loan).toString()
    : "0";
  const secondLoanPandI = carriesNewSecond
    ? second_mortgage_payment_p_and_i(
        documentType,
        loan,
        property,
        property_to_be_sold,
        is_selling_property
      )
    : new Decimal(0);

  return (
    <table className="info-table">
      <colgroup>
        <col />
        <col />
        <col />
        <col />
        <col />
        <col />
        <col />
        <col />
        <col />
      </colgroup>
      <tbody className="no-top-border no-bottom-border with-border with-td-border">
        <tr>
          <td className="no-border summary-header">Loan Terms</td>
          <td className="no-border" colSpan="2" />
          <td className="no-border normal-font gray-bg" colSpan="3">
            Can this amount increase after closing?
          </td>
          <td className="no-border" colSpan="3" />
        </tr>
        <tr>
          <td className="bold">Loan Amount</td>
          <td className="center-align no-right-border" colSpan="2">
            <CurrencyField naked fixedDigits={0} value={disclosedLoanAmount} />
          </td>
          <td className="center-align no-right-border no-left-border">No</td>
          <td className="no-right-border no-left-border" colSpan="5" />
        </tr>
        <tr>
          <td className="bold">Interest Rate</td>
          <td
            className="center-align no-left-border no-right-border"
            colSpan="2"
            rowSpan="3"
          >
            <PercentField naked decimalPlaces={3} value={disclosedRate} />
          </td>
          <td className="center-align no-left-border" rowSpan="3">
            {rateCanIncrease ? "Yes" : "No"}
          </td>
          <td className="no-border" colSpan="3">
            {isArm && "Adjust Every Year starting in year"}
            {isBuydownRate && "Adjusts every year starting in year"}
            {isVariableSecond &&
              "The Interest Rate is a Variable based on the Index associated. The calculation is; Index + Margin = Rate. The Rate may go up or down."}
          </td>
          <td className="center-align no-border">
            {isArm && (
              <NumberField naked value={arm_adjust_starting_in_year(loan)} />
            )}
            {isBuydownRate && <NumberField naked value={2} />}
          </td>
          <td className="no-border" />
        </tr>
        <tr>
          <td rowSpan="2" />
          <td className="no-border">
            {(isArm || isBuydownRate) && "As High as"}
          </td>
          <td className="center-align no-border">
            {isArm && (
              <PercentField
                naked
                fixedDigits={3}
                value={arm_adjust_as_high_as_rate(loan)}
              />
            )}
            {isBuydownRate && (
              <PercentField naked fixedDigits={3} value={loan.rate} />
            )}
          </td>
          <td className="center-align no-border">
            {(isArm || isBuydownRate) && "in year"}
          </td>
          <td className="center-align no-border">
            {isArm && (
              <NumberField naked value={arm_adjust_starting_in_year(loan)} />
            )}
            {isBuydownRate && <NumberField naked value={buydownFullYear} />}
          </td>
          <td className="no-border" />
        </tr>
        <tr>
          <td className="bottom-border no-border" colSpan="5">
            {(isArm || isBuydownRate) && "See AIR table on page 2 for details"}
          </td>
        </tr>
        {carriesNewSecond && (
          <tr>
            <td className="bold">New 2nd Loan Amount</td>
            <td className="center-align no-right-border" colSpan="2">
              <CurrencyField naked fixedDigits={0} value={secondLoanAmount} />
            </td>
            <td className="center-align no-right-border no-left-border">No</td>
            <td className="no-right-border no-left-border" colSpan="5" />
          </tr>
        )}
        {carriesNewSecond && (
          <tr>
            <td className="bold">New 2nd Interest Rate</td>
            <td
              className="center-align no-left-border no-right-border"
              colSpan="2"
            >
              <PercentField naked decimalPlaces={3} value={secondLoanRate} />
            </td>
            <td className="center-align no-left-border">
              {isVariableSecondCombo ? "Yes" : "No"}
            </td>
            <td className="no-border" colSpan="5">
              {isVariableSecondCombo &&
                "The Interest Rate is a Variable based on the Index associated. The calculation is; Index + Margin = Rate. The Rate may go up or down."}
            </td>
          </tr>
        )}
        <tr>
          <td className="no-bottom-border bold">
            Monthly Principal & Interest
          </td>
          <td
            className="center-align no-left-border no-right-border no-bottom-border"
            colSpan="2"
            rowSpan="3"
          >
            <CurrencyField
              naked
              fixedDigits={0}
              value={disclosedPrincipalAndInterest}
            />
          </td>
          <td
            className="center-align no-left-border no-bottom-border"
            rowSpan="3"
          >
            {piCanIncrease ? "Yes" : "No"}
          </td>
          <td className="no-border" colSpan="3">
            {isInterestOnly && <div>* Adjusts once in Year</div>}
            {isArm && <div>Adjust Every Year starting in year</div>}
            {isBuydown && <div>Adjusts every year starting in year</div>}
            {isVariableSecond && (
              <div>
                The monthly payment is based on the Interest Rate and subject to
                the market Direction. The payment may go up or down.
              </div>
            )}
          </td>
          <td className="no-border center-align">
            {isInterestOnly && (
              <div>
                <NumberField
                  naked
                  value={month_to_year(loan.interest_only_period).add(1)}
                />
              </div>
            )}
            {isArm && (
              <div>
                <NumberField naked value={arm_adjust_starting_in_year(loan)} />
              </div>
            )}
            {isBuydown && (
              <div>
                <NumberField naked value={2} />
              </div>
            )}
          </td>
          <td className="no-border" />
        </tr>
        <tr>
          <td className="no-top-border no-bottom-border smaller-font italic">
            See Projected Payments Below
          </td>
          <td className="no-border" colSpan="2">
            {isInterestOnly && <div>* Goes as high as</div>}
            {isArm && <div>Can go as High as</div>}
            {isBuydown && <div>Can go as High as</div>}
          </td>
          <td className="center-align no-border">
            {isInterestOnly && (
              <div>
                <CurrencyField
                  naked
                  fixedDigits={0}
                  value={first_mortgage_payment_p_and_i(
                    documentType,
                    loan,
                    property,
                    property_to_be_sold,
                    is_selling_property
                  )}
                />
              </div>
            )}
            {isArm && (
              <div>
                <PercentField
                  naked
                  fixedDigits={3}
                  value={arm_adjust_as_high_as_rate(loan)}
                />
              </div>
            )}
            {isBuydown && (
              <div>
                <CurrencyField
                  naked
                  fixedDigits={0}
                  value={first_mortgage_payment_p_and_i(
                    documentType,
                    loan,
                    property,
                    property_to_be_sold,
                    is_selling_property
                  )}
                />
              </div>
            )}
          </td>
          <td className="center-align no-border">
            {isInterestOnly && <div>in year</div>}
            {isArm && <div>in year</div>}
            {isBuydown && <div>in year</div>}
          </td>
          <td className="center-align no-border">
            {isInterestOnly && (
              <div>
                <NumberField
                  naked
                  value={month_to_year(loan.interest_only_period).add(1)}
                />
              </div>
            )}
            {isArm && (
              <div>
                <NumberField naked value={arm_adjust_starting_in_year(loan)} />
              </div>
            )}
            {isBuydown && (
              <div>
                <NumberField naked value={buydownFullYear} />
              </div>
            )}
          </td>
        </tr>
        <tr>
          <td className="no-top-border no-bottom-border smaller-font italic">
            for Your Total Monthly Payment
          </td>
          <td className="no-border" colSpan="3">
            {isInterestOnly && (
              <div>* Includes only interest and no principal until year</div>
            )}
            {isArm && <div>See AIR table on page 2 for details</div>}
            {isBuydownRate && <div>See AIR table on page 2 for details</div>}
            {isBuydownPayment && <div>See AP table on page 2 for details</div>}
          </td>
          <td className="center-align no-border">
            {isInterestOnly && (
              <NumberField
                naked
                value={month_to_year(loan.interest_only_period).add(1)}
              />
            )}
          </td>
          <td className="no-border" />
        </tr>
        {isInterestOnly && (
          <tr>
            <td className="no-top-border no-bottom-border" />
            <td
              className="no-left-border no-top-border no-bottom-border"
              colSpan="3"
            />
            <td className="no-border" colSpan="5">
              * See AP Table on page 2 for details
            </td>
          </tr>
        )}
        {carriesNewSecond && (
          <tr>
            <td className="bold">New 2nd Monthly P&amp;I</td>
            <td
              className="center-align no-left-border no-right-border"
              colSpan="2"
            >
              <CurrencyField naked fixedDigits={0} value={secondLoanPandI} />
            </td>
            <td className="center-align no-left-border">
              {isVariableSecondCombo ? "Yes" : "No"}
            </td>
            <td className="no-border" colSpan="5">
              {isVariableSecondCombo &&
                "The monthly payment is based on the Interest Rate and subject to the market Direction. The payment may go up or down."}
            </td>
          </tr>
        )}
        <tr>
          <td className="no-bottom-border" />
          <td className="top-border no-border" colSpan="2" />
          <td className="top-border no-border normal-font gray-bg" colSpan="3">
            Does the loan have these features?
          </td>
          <td className="top-border no-border" colSpan="3" />
        </tr>
        <tr>
          <td className="no-border right-border bold">Prepayment Penalty</td>
          <td className="no-border" colSpan="2" />
          <td className="center-align no-border">
            {YES_NO_TBD[loan.prepayment_penalty]}
          </td>
          <td className="no-border" colSpan="4">
            {loan.prepayment_penalty === YES &&
              "Prepayment Penalty Amount will vary depending on Terms:"}
          </td>
          <td className="no-border">
            {loan.prepayment_penalty === YES && loan.prepayment_terms}
          </td>
        </tr>
        <tr>
          <td className="bold">Balloon Payment</td>
          <td className="no-right-border" colSpan="2" />
          <td className="no-left-border no-right-border center-align">
            {YES_NO_TBD[loan.balloon_payment]}
          </td>
          <td className="no-left-border no-right-border" colSpan="4">
            {loan.balloon_payment === YES &&
              "The Balloon Payment Amount will be due month"}
          </td>
          <td className="no-left-border">
            {loan.balloon_payment === YES && loan.balloon_payment_period}
          </td>
        </tr>
        <tr>
          <td className="no-border" colSpan="9" />
        </tr>
      </tbody>
    </table>
  );
}
